import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container, Paper, Typography, Box, Button, Alert, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, IconButton, Tooltip, LinearProgress, Chip,
  ToggleButtonGroup, ToggleButton, Collapse, FormControlLabel, Checkbox,
  TableSortLabel, Tabs, Tab, Link,
} from '@mui/material';
import {
  Refresh, Search, Clear, ExpandMore, ExpandLess, Inventory2,
  WarningAmber, ErrorOutline, ShoppingCart, TableChart, Insights,
  ReportProblem, Edit,
} from '@mui/icons-material';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as ChartTooltip, Legend as ChartLegend, ReferenceLine, Cell,
} from 'recharts';
import { Link as RouterLink } from 'react-router-dom';
import { manufacturingAPI } from '../services/api';
import { toast } from 'react-toastify';
import usePageTitle from '../hooks/usePageTitle';

const STATUS = {
  SHORT: { order: 0, label: 'Short for orders', color: '#d32f2f' },
  ORDER_NOW: { order: 1, label: 'Order now', color: '#ef6c00' },
  ORDER_SOON: { order: 2, label: 'Order soon', color: '#f9a825' },
  OK: { order: 3, label: 'OK', color: '#2e7d32' },
  NO_USAGE: { order: 4, label: 'No recent usage', color: '#757575' },
};

const fmtQty = (v) => {
  if (v == null) return '-';
  const n = Number(v);
  if (Math.abs(n - Math.round(n)) < 0.001) return Math.round(n).toLocaleString();
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const fmtDate = (d) =>
  d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

/**
 * Derive forecast fields for one material.
 *  - perDay: avg daily usage for the selected window
 *  - runOutDays / runOutDate: when current stock hits zero at that usage rate
 *  - orderByDate: run-out minus supplier lead time
 *  - status: traffic light
 * The material's own lead_time_days wins over the page default.
 */
const buildForecast = (m, usageWindow, defaultLeadDays) => {
  const perDay = m.usage?.[String(usageWindow)]?.per_day || 0;
  const shortNow = m.shortfall > 0;
  const leadDays = m.lead_time_days != null ? m.lead_time_days : defaultLeadDays;
  const hasOwnLead = m.lead_time_days != null;

  let runOutDays = null;
  if (perDay > 0) {
    runOutDays = m.on_hand / perDay;
  }

  const now = new Date();
  const runOutDate = runOutDays != null ? addDays(now, runOutDays) : null;
  const orderByDate = runOutDays != null ? addDays(now, runOutDays - leadDays) : null;

  let status;
  if (shortNow) {
    status = STATUS.SHORT;
  } else if (runOutDays == null) {
    status = STATUS.NO_USAGE;
  } else if (runOutDays <= leadDays) {
    status = STATUS.ORDER_NOW;
  } else if (runOutDays <= leadDays * 2) {
    status = STATUS.ORDER_SOON;
  } else {
    status = STATUS.OK;
  }

  const belowReorder = m.reorder_level > 0 && m.on_hand <= m.reorder_level;

  return { perDay, runOutDays, runOutDate, orderByDate, status, belowReorder, leadDays, hasOwnLead };
};

/**
 * Simulate fulfilling all order demand in due-date order against current
 * stock, per material. Returns one entry per order with the materials it
 * needs and how much of each it would be short once earlier orders have
 * taken their share.
 */
const analyzeOrders = (materials) => {
  const orderMap = new Map();

  for (const m of materials) {
    if (!m.orders || m.orders.length === 0) continue;
    const rows = [...m.orders].sort((a, b) => {
      const da = a.due_date || '9999-12-31';
      const db = b.due_date || '9999-12-31';
      if (da !== db) return da < db ? -1 : 1;
      return (a.order_number || '').localeCompare(b.order_number || '');
    });
    let cumulative = 0;
    for (const r of rows) {
      cumulative += r.qty;
      const short = Math.max(0, Math.min(r.qty, cumulative - m.on_hand));

      let entry = orderMap.get(r.order_number);
      if (!entry) {
        entry = {
          order_number: r.order_number,
          order_id: r.order_id,
          customer_name: r.customer_name,
          due_date: r.due_date,
          materials: [],
        };
        orderMap.set(r.order_number, entry);
      }
      if (r.due_date && (!entry.due_date || r.due_date < entry.due_date)) {
        entry.due_date = r.due_date;
      }
      entry.materials.push({
        item_code: m.item_code,
        name: m.name,
        uom: m.unit_of_measure,
        on_hand: m.on_hand,
        needed: r.qty,
        short,
        product_code: r.product_code,
      });
    }
  }

  const orders = [...orderMap.values()];
  for (const o of orders) {
    // An order can reach the same material through several lines — merge
    const byCode = new Map();
    for (const mt of o.materials) {
      const existing = byCode.get(mt.item_code);
      if (existing) {
        existing.needed += mt.needed;
        existing.short += mt.short;
      } else {
        byCode.set(mt.item_code, { ...mt });
      }
    }
    o.materials = [...byCode.values()].sort((a, b) => (b.short - a.short) || (b.needed - a.needed));
    o.shortMaterials = o.materials.filter(mt => mt.short > 0.001);
    o.atRisk = o.shortMaterials.length > 0;
  }

  orders.sort((a, b) => {
    if (a.atRisk !== b.atRisk) return a.atRisk ? -1 : 1;
    const da = a.due_date || '9999-12-31';
    const db = b.due_date || '9999-12-31';
    if (da !== db) return da < db ? -1 : 1;
    return (a.order_number || '').localeCompare(b.order_number || '');
  });
  return orders;
};

function SummaryCard({ title, value, caption, color, icon }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography color="text.secondary" variant="body2">{title}</Typography>
        </Box>
        <Typography variant="h4" sx={{ color, fontWeight: 700 }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{caption}</Typography>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Materials tab
// ---------------------------------------------------------------------------

/** Inline editor for a material's own supplier lead time. */
function LeadTimeCell({ m, forecast, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = (e) => {
    e.stopPropagation();
    setValue(m.lead_time_days != null ? String(m.lead_time_days) : '');
    setEditing(true);
  };

  const save = async () => {
    const v = value.trim() === '' ? null : Math.max(0, parseInt(value, 10) || 0);
    if (v === (m.lead_time_days ?? null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await manufacturingAPI.procurement.setLeadTime(m.item_id, v);
      onSaved(m.item_id, v);
      setEditing(false);
    } catch (err) {
      console.error('Failed to save lead time:', err);
      toast.error('Failed to save lead time');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        <TextField
          size="small"
          type="number"
          value={value}
          autoFocus
          disabled={saving}
          onChange={(e) => setValue(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') setEditing(false);
          }}
          placeholder="default"
          inputProps={{ min: 0, style: { width: 52, padding: '4px 8px', textAlign: 'right' } }}
        />
      </TableCell>
    );
  }

  return (
    <TableCell
      align="right"
      onClick={startEdit}
      sx={{ cursor: 'pointer', '&:hover .lead-edit': { opacity: 1 } }}
    >
      <Tooltip title={forecast.hasOwnLead ? 'Item-specific lead time — click to change' : 'Using page default — click to set for this item'}>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            component="span"
            fontWeight={forecast.hasOwnLead ? 700 : 400}
            color={forecast.hasOwnLead ? 'text.primary' : 'text.disabled'}
          >
            {forecast.leadDays}d
          </Typography>
          <Edit className="lead-edit" sx={{ fontSize: 13, color: 'text.disabled', opacity: 0, transition: 'opacity 0.15s' }} />
        </Box>
      </Tooltip>
    </TableCell>
  );
}

function MaterialRow({ m, forecast, usageWindow, onLeadTimeSaved }) {
  const [open, setOpen] = useState(false);
  const f = forecast;
  const hasOrders = (m.orders || []).length > 0;

  return (
    <>
      <TableRow
        hover
        onClick={() => hasOrders && setOpen(!open)}
        sx={{
          cursor: hasOrders ? 'pointer' : 'default',
          borderLeft: '4px solid',
          borderLeftColor: f.status.color,
          backgroundColor: f.status === STATUS.SHORT ? 'rgba(211,47,47,0.05)' : 'inherit',
        }}
      >
        <TableCell sx={{ width: 34, px: 0.5 }}>
          {hasOrders && (
            <IconButton size="small">
              {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
            {m.item_code}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.name}>
            {m.name}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={600}>{fmtQty(m.on_hand)}</Typography>
          <Typography variant="caption" color="text.secondary">{m.unit_of_measure}</Typography>
          {f.belowReorder && (
            <Tooltip title={`Below reorder level (${fmtQty(m.reorder_level)})`}>
              <WarningAmber sx={{ fontSize: 14, color: '#ef6c00', ml: 0.5, verticalAlign: 'middle' }} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={600}>{fmtQty(m.required_for_orders)}</Typography>
          {hasOrders && (
            <Typography variant="caption" color="text.secondary">
              {m.orders.length} order line{m.orders.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">
          {m.shortfall > 0 ? (
            <Typography variant="body2" fontWeight={700} color="error.main">
              {fmtQty(m.shortfall)}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.disabled">-</Typography>
          )}
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2">{f.perDay > 0 ? fmtQty(f.perDay) : '-'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {fmtQty(m.usage?.[String(usageWindow)]?.total || 0)} in {usageWindow}d
          </Typography>
        </TableCell>
        <TableCell align="right">
          {f.runOutDays != null ? (
            <>
              <Typography variant="body2" fontWeight={600} color={f.runOutDays <= 14 ? 'error.main' : 'inherit'}>
                {Math.floor(f.runOutDays)}d
              </Typography>
              <Typography variant="caption" color="text.secondary">{fmtDate(f.runOutDate)}</Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.disabled">-</Typography>
          )}
        </TableCell>
        <TableCell align="right">
          {f.orderByDate != null ? (
            <Typography
              variant="body2"
              fontWeight={600}
              color={f.orderByDate <= new Date() ? 'error.main' : 'inherit'}
            >
              {f.orderByDate <= new Date() ? 'Overdue' : fmtDate(f.orderByDate)}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.disabled">-</Typography>
          )}
        </TableCell>
        <LeadTimeCell m={m} forecast={f} onSaved={onLeadTimeSaved} />
        <TableCell>
          <Chip
            label={f.status.label}
            size="small"
            sx={{ backgroundColor: f.status.color, color: '#fff', fontWeight: 700, height: 22 }}
          />
        </TableCell>
      </TableRow>
      {hasOrders && (
        <TableRow>
          <TableCell colSpan={10} sx={{ py: 0, border: 0 }}>
            <Collapse in={open} unmountOnExit>
              <Box sx={{ py: 1.5, px: 4, backgroundColor: 'grey.50', borderRadius: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Demand from current orders
                </Typography>
                <Table size="small" sx={{ maxWidth: 760, '& th, & td': { px: 1, py: 0.5 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Order #</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Due</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Units to make</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{m.item_code} needed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {m.orders.map((o, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontWeight: 600 }}>{o.order_number}</TableCell>
                        <TableCell>{o.due_date ? fmtDate(new Date(o.due_date + 'T00:00:00')) : '-'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{o.product_code}</TableCell>
                        <TableCell align="right">{fmtQty(o.product_qty)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtQty(o.qty)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Charts tab
// ---------------------------------------------------------------------------

function ChartPaper({ title, subtitle, height, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {subtitle}
      </Typography>
      <ResponsiveContainer width="100%" height={height}>
        {children}
      </ResponsiveContainer>
    </Paper>
  );
}

function ChartsTab({ rows, usageWindow, leadDays }) {
  const TOP_N = 15;

  const shortfallData = useMemo(() =>
    rows
      .filter(({ m }) => m.shortfall > 0)
      .sort((a, b) => b.m.shortfall - a.m.shortfall)
      .slice(0, TOP_N)
      .map(({ m }) => ({
        code: m.item_code,
        name: m.name,
        'In stock': Math.round(m.on_hand * 100) / 100,
        'Required for orders': Math.round(m.required_for_orders * 100) / 100,
      })),
    [rows]
  );

  const runOutData = useMemo(() =>
    rows
      .filter(({ f }) => f.runOutDays != null)
      .sort((a, b) => a.f.runOutDays - b.f.runOutDays)
      .slice(0, TOP_N)
      .map(({ m, f }) => ({
        code: m.item_code,
        name: m.name,
        days: Math.round(f.runOutDays * 10) / 10,
        color: f.status.color,
      })),
    [rows]
  );

  const usageData = useMemo(() =>
    rows
      .filter(({ f }) => f.perDay > 0)
      .sort((a, b) => b.f.perDay - a.f.perDay)
      .slice(0, TOP_N)
      .map(({ m, f }) => ({
        code: m.item_code,
        name: m.name,
        perDay: Math.round(f.perDay * 100) / 100,
      })),
    [rows]
  );

  const axisTick = { fontSize: 11, fontFamily: 'monospace' };
  const chartHeight = (n) => Math.max(160, n * 28 + 60);

  const tooltipLabel = (data) => (label) => {
    const row = data.find(d => d.code === label);
    return row ? `${label} — ${row.name}` : label;
  };

  if (shortfallData.length === 0 && runOutData.length === 0 && usageData.length === 0) {
    return (
      <Alert severity="info">Nothing to chart yet — no shortfalls and no recorded usage.</Alert>
    );
  }

  return (
    <Box>
      {shortfallData.length > 0 ? (
        <ChartPaper
          title="Biggest shortfalls for current orders"
          subtitle="Stock on hand vs the quantity required to make everything currently on order"
          height={chartHeight(shortfallData.length)}
        >
          <BarChart data={shortfallData} layout="vertical" margin={{ left: 40, right: 24 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="code" width={110} tick={axisTick} />
            <ChartTooltip labelFormatter={tooltipLabel(shortfallData)} formatter={(v) => v.toLocaleString()} />
            <ChartLegend />
            <Bar dataKey="In stock" fill="#66bb6a" radius={[0, 3, 3, 0]} barSize={10} />
            <Bar dataKey="Required for orders" fill="#5c6bc0" radius={[0, 3, 3, 0]} barSize={10} />
          </BarChart>
        </ChartPaper>
      ) : (
        <Alert severity="success" sx={{ mb: 2 }}>
          No materials are short for current orders.
        </Alert>
      )}

      {runOutData.length > 0 && (
        <ChartPaper
          title="Run-out horizon"
          subtitle={`Days of stock left at the average daily usage over the last ${usageWindow} days. The dashed line is the ${leadDays}-day default lead time — bar colours use each item's own lead time where set.`}
          height={chartHeight(runOutData.length)}
        >
          <BarChart data={runOutData} layout="vertical" margin={{ left: 40, right: 40 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} unit="d" />
            <YAxis type="category" dataKey="code" width={110} tick={axisTick} />
            <ChartTooltip
              labelFormatter={tooltipLabel(runOutData)}
              formatter={(v) => [`${v} days`, 'Stock lasts']}
            />
            <ReferenceLine x={leadDays} stroke="#d32f2f" strokeDasharray="4 4" label={{ value: 'Lead time', fill: '#d32f2f', fontSize: 11, position: 'top' }} />
            <Bar dataKey="days" radius={[0, 3, 3, 0]} barSize={14}>
              {runOutData.map((d) => <Cell key={d.code} fill={d.color} />)}
            </Bar>
          </BarChart>
        </ChartPaper>
      )}

      {usageData.length > 0 && (
        <ChartPaper
          title={`Fastest moving materials (last ${usageWindow} days)`}
          subtitle="Average consumption per day from stock transactions"
          height={chartHeight(usageData.length)}
        >
          <BarChart data={usageData} layout="vertical" margin={{ left: 40, right: 24 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="code" width={110} tick={axisTick} />
            <ChartTooltip
              labelFormatter={tooltipLabel(usageData)}
              formatter={(v) => [`${v.toLocaleString()} / day`, 'Avg usage']}
            />
            <Bar dataKey="perDay" fill="#26a69a" radius={[0, 3, 3, 0]} barSize={14} />
          </BarChart>
        </ChartPaper>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Orders at risk tab
// ---------------------------------------------------------------------------

function OrderRow({ order }) {
  const [open, setOpen] = useState(false);
  const shown = order.shortMaterials.slice(0, 3);
  const extra = order.shortMaterials.length - shown.length;

  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{
          cursor: 'pointer',
          borderLeft: '4px solid',
          borderLeftColor: order.atRisk ? STATUS.SHORT.color : STATUS.OK.color,
          backgroundColor: order.atRisk ? 'rgba(211,47,47,0.04)' : 'inherit',
        }}
      >
        <TableCell sx={{ width: 34, px: 0.5 }}>
          <IconButton size="small">
            {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          {order.order_id ? (
            <Link
              component={RouterLink}
              to={`/dispatch/orders/${order.order_id}`}
              onClick={(e) => e.stopPropagation()}
              sx={{ fontWeight: 700 }}
            >
              {order.order_number}
            </Link>
          ) : (
            <Typography variant="body2" fontWeight={700}>{order.order_number}</Typography>
          )}
        </TableCell>
        <TableCell>{order.customer_name}</TableCell>
        <TableCell>
          {order.due_date ? fmtDate(new Date(order.due_date + 'T00:00:00')) : '-'}
        </TableCell>
        <TableCell align="right">{order.materials.length}</TableCell>
        <TableCell>
          {order.atRisk ? (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {shown.map(mt => (
                <Chip
                  key={mt.item_code}
                  label={`${mt.item_code} -${fmtQty(mt.short)}`}
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem', backgroundColor: 'rgba(211,47,47,0.1)', color: '#d32f2f', fontWeight: 700, fontFamily: 'monospace' }}
                />
              ))}
              {extra > 0 && (
                <Typography variant="caption" color="text.secondary">+{extra} more</Typography>
              )}
            </Box>
          ) : (
            <Typography variant="caption" color="text.disabled">-</Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip
            label={order.atRisk ? `At risk (${order.shortMaterials.length})` : 'Covered'}
            size="small"
            sx={{
              backgroundColor: order.atRisk ? STATUS.SHORT.color : STATUS.OK.color,
              color: '#fff', fontWeight: 700, height: 22,
            }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 1.5, px: 4, backgroundColor: 'grey.50', borderRadius: 1, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Raw materials for this order
              </Typography>
              <Table size="small" sx={{ maxWidth: 720, '& th, & td': { px: 1, py: 0.5 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Material</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>In stock</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>This order needs</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: STATUS.SHORT.color }}>Short by</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.materials.map((mt) => (
                    <TableRow key={mt.item_code}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {mt.item_code}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{mt.name}</Typography>
                      </TableCell>
                      <TableCell align="right">{fmtQty(mt.on_hand)} {mt.uom}</TableCell>
                      <TableCell align="right">{fmtQty(mt.needed)}</TableCell>
                      <TableCell align="right">
                        {mt.short > 0.001 ? (
                          <Typography variant="body2" fontWeight={700} color="error.main">
                            {fmtQty(mt.short)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function OrdersTab({ orders }) {
  const [showCovered, setShowCovered] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const atRisk = orders.filter(o => o.atRisk);

  const visible = useMemo(() => {
    let list = showCovered ? orders : orders.filter(o => o.atRisk);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(o =>
        (o.order_number || '').toLowerCase().includes(q) ||
        (o.customer_name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [orders, showCovered, searchQuery]);

  return (
    <Box>
      <Alert severity={atRisk.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
        {atRisk.length > 0 ? (
          <><strong>{atRisk.length}</strong> of {orders.length} open orders would hit raw material shortages
            if everything is made in due-date order with current stock.</>
        ) : (
          <>All {orders.length} open orders are covered by current raw material stock.</>
        )}
      </Alert>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search orders or customers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}><Clear /></IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={showCovered} onChange={(e) => setShowCovered(e.target.checked)} />}
          label="Show covered orders too"
        />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          Showing {visible.length} of {orders.length} orders · earlier due dates get stock first
        </Typography>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main', '& th': { color: 'white', fontWeight: 'bold' } }}>
              <TableCell sx={{ width: 34, px: 0.5 }} />
              <TableCell>Order #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell align="right">Materials used</TableCell>
              <TableCell>Materials short</TableCell>
              <TableCell sx={{ width: 130 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <ReportProblem sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    {orders.length === 0
                      ? 'No open orders with raw material demand.'
                      : showCovered
                        ? 'No orders match the search.'
                        : 'No orders at risk — try "Show covered orders too".'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {visible.map(o => <OrderRow key={o.order_number} order={o} />)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const StockProcurement = () => {
  usePageTitle('Stock Procurement');

  const [materials, setMaterials] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const [searchQuery, setSearchQuery] = useState('');
  const [usageWindow, setUsageWindow] = useState(() =>
    parseInt(localStorage.getItem('procurement_usageWindow'), 10) || 30
  );
  const [leadDays, setLeadDays] = useState(() => {
    const saved = parseInt(localStorage.getItem('procurement_leadDays'), 10);
    return Number.isNaN(saved) ? 14 : saved;
  });
  const [attentionOnly, setAttentionOnly] = useState(
    () => localStorage.getItem('procurement_attentionOnly') === 'true'
  );
  const [sortBy, setSortBy] = useState('urgency');
  const [sortDir, setSortDir] = useState('asc');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await manufacturingAPI.procurement.rawMaterials();
      setMaterials(res.data.materials || []);
      setGeneratedAt(res.data.generated_at ? new Date(res.data.generated_at) : new Date());
    } catch (err) {
      console.error('Failed to load procurement data:', err);
      setError('Failed to load procurement data');
      toast.error('Failed to load procurement data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUsageWindow = (e, val) => {
    if (!val) return;
    setUsageWindow(val);
    localStorage.setItem('procurement_usageWindow', String(val));
  };

  const handleLeadDays = (e) => {
    const v = Math.max(0, parseInt(e.target.value, 10) || 0);
    setLeadDays(v);
    localStorage.setItem('procurement_leadDays', String(v));
  };

  const toggleAttentionOnly = (e) => {
    setAttentionOnly(e.target.checked);
    localStorage.setItem('procurement_attentionOnly', String(e.target.checked));
  };

  const handleLeadTimeSaved = useCallback((itemId, leadTimeDays) => {
    setMaterials(prev => prev.map(m =>
      m.item_id === itemId ? { ...m, lead_time_days: leadTimeDays } : m
    ));
  }, []);

  // Forecast every material once per (data, window, lead time) change
  const rows = useMemo(() =>
    materials.map(m => ({ m, f: buildForecast(m, usageWindow, leadDays) })),
    [materials, usageWindow, leadDays]
  );

  const orderAnalysis = useMemo(() => analyzeOrders(materials), [materials]);
  const ordersAtRisk = useMemo(() => orderAnalysis.filter(o => o.atRisk).length, [orderAnalysis]);

  const summary = useMemo(() => ({
    short: rows.filter(r => r.f.status === STATUS.SHORT).length,
    orderNow: rows.filter(r => r.f.status === STATUS.ORDER_NOW).length,
    orderSoon: rows.filter(r => r.f.status === STATUS.ORDER_SOON).length,
    total: rows.length,
  }), [rows]);

  const visibleRows = useMemo(() => {
    let list = rows;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(({ m }) =>
        m.item_code.toLowerCase().includes(q) || (m.name || '').toLowerCase().includes(q)
      );
    }
    if (attentionOnly) {
      list = list.filter(({ f }) =>
        f.status === STATUS.SHORT || f.status === STATUS.ORDER_NOW ||
        f.status === STATUS.ORDER_SOON || f.belowReorder
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    const sorted = [...list].sort((a, b) => {
      switch (sortBy) {
        case 'item':
          return dir * a.m.item_code.localeCompare(b.m.item_code);
        case 'on_hand':
          return dir * (a.m.on_hand - b.m.on_hand);
        case 'required':
          return dir * (a.m.required_for_orders - b.m.required_for_orders);
        case 'shortfall':
          return dir * (a.m.shortfall - b.m.shortfall);
        case 'usage':
          return dir * (a.f.perDay - b.f.perDay);
        case 'run_out':
          // nulls (no usage) always sort last
          if (a.f.runOutDays == null && b.f.runOutDays == null) return 0;
          if (a.f.runOutDays == null) return 1;
          if (b.f.runOutDays == null) return -1;
          return dir * (a.f.runOutDays - b.f.runOutDays);
        case 'urgency':
        default: {
          const so = a.f.status.order - b.f.status.order;
          if (so !== 0) return dir * so;
          const sf = b.m.shortfall - a.m.shortfall;
          if (sf !== 0) return dir * sf;
          const ar = a.f.runOutDays == null ? Infinity : a.f.runOutDays;
          const br = b.f.runOutDays == null ? Infinity : b.f.runOutDays;
          return dir * (ar - br);
        }
      }
    });
    return sorted;
  }, [rows, searchQuery, attentionOnly, sortBy, sortDir]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const sortLabel = (key, label) => (
    <TableSortLabel
      active={sortBy === key}
      direction={sortBy === key ? sortDir : 'asc'}
      onClick={() => handleSort(key)}
      sx={{ '&.Mui-active': { color: 'inherit' }, '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCart color="primary" />
            Stock Procurement
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Raw material demand for current orders, usage trends and run-out forecasts
            {generatedAt && ` — calculated ${generatedAt.toLocaleTimeString('en-GB')}`}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2.4}>
          <SummaryCard
            title="Short for current orders"
            value={summary.short}
            caption="Not enough stock to cover orders"
            color={STATUS.SHORT.color}
            icon={<ErrorOutline sx={{ color: STATUS.SHORT.color }} />}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <SummaryCard
            title="Order now"
            value={summary.orderNow}
            caption="Run out within lead time"
            color={STATUS.ORDER_NOW.color}
            icon={<WarningAmber sx={{ color: STATUS.ORDER_NOW.color }} />}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <SummaryCard
            title="Order soon"
            value={summary.orderSoon}
            caption="Run out within 2× lead time"
            color={STATUS.ORDER_SOON.color}
            icon={<WarningAmber sx={{ color: STATUS.ORDER_SOON.color }} />}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <SummaryCard
            title="Orders at risk"
            value={ordersAtRisk}
            caption="Would hit a material shortage"
            color={ordersAtRisk > 0 ? STATUS.SHORT.color : STATUS.OK.color}
            icon={<ReportProblem sx={{ color: ordersAtRisk > 0 ? STATUS.SHORT.color : STATUS.OK.color }} />}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <SummaryCard
            title="Raw materials tracked"
            value={summary.total}
            caption="With order demand or recent usage"
            color="text.primary"
            icon={<Inventory2 color="action" />}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" allowScrollButtonsMobile>
          <Tab icon={<TableChart />} iconPosition="start" label="Materials" />
          <Tab icon={<Insights />} iconPosition="start" label="Charts" />
          <Tab
            icon={<ReportProblem />}
            iconPosition="start"
            label={`Orders at Risk${ordersAtRisk > 0 ? ` (${ordersAtRisk})` : ''}`}
            sx={ordersAtRisk > 0 ? { color: STATUS.SHORT.color, '&.Mui-selected': { color: STATUS.SHORT.color } } : undefined}
          />
        </Tabs>
      </Paper>

      {/* Controls (materials + charts tabs) */}
      {activeTab !== 2 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {activeTab === 0 && (
            <TextField
              size="small"
              placeholder="Search materials..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 240 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}><Clear /></IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Usage basis:</Typography>
            <ToggleButtonGroup value={usageWindow} exclusive onChange={handleUsageWindow} size="small">
              <ToggleButton value={7}>7 days</ToggleButton>
              <ToggleButton value={30}>30 days</ToggleButton>
              <ToggleButton value={90}>90 days</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Tooltip title="Used for any material without its own lead time. Set per-item lead times in the Lead time column.">
            <TextField
              size="small"
              label="Default lead time (days)"
              type="number"
              value={leadDays}
              onChange={handleLeadDays}
              sx={{ width: 180 }}
              inputProps={{ min: 0 }}
            />
          </Tooltip>
          {activeTab === 0 && (
            <>
              <FormControlLabel
                control={<Checkbox size="small" checked={attentionOnly} onChange={toggleAttentionOnly} />}
                label="Needs attention only"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                Showing {visibleRows.length} of {rows.length} materials
              </Typography>
            </>
          )}
        </Paper>
      )}

      {/* --- Materials tab --- */}
      {activeTab === 0 && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {[STATUS.SHORT, STATUS.ORDER_NOW, STATUS.ORDER_SOON, STATUS.OK, STATUS.NO_USAGE].map(s => (
              <Box key={s.label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: s.color }} />
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Box>
            ))}
            <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
              Run-out = stock ÷ avg daily usage · Order by = run-out − lead time · Click a row for the order breakdown
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small" sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main', '& th': { color: 'white', fontWeight: 'bold' } }}>
                  <TableCell sx={{ width: 34, px: 0.5 }} />
                  <TableCell>{sortLabel('item', 'Raw Material')}</TableCell>
                  <TableCell align="right">{sortLabel('on_hand', 'In Stock')}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Total needed to make the unallocated, unshipped quantity on all active customer orders">
                      <span>{sortLabel('required', 'Required (orders)')}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{sortLabel('shortfall', 'Shortfall')}</TableCell>
                  <TableCell align="right">{sortLabel('usage', `Avg/day (${usageWindow}d)`)}</TableCell>
                  <TableCell align="right">{sortLabel('run_out', 'Est. run-out')}</TableCell>
                  <TableCell align="right">Order by</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Item-specific supplier lead time. Click a value to edit; grey = using the page default.">
                      <span>Lead time</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ width: 130 }}>{sortLabel('urgency', 'Status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ opacity: loading && rows.length > 0 ? 0.45 : 1, transition: 'opacity 0.15s' }}>
                {!loading && visibleRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                      <Inventory2 sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">
                        {rows.length === 0
                          ? 'No raw materials with order demand or recent usage found.'
                          : 'No materials match the current filters.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {visibleRows.map(({ m, f }) => (
                  <MaterialRow
                    key={m.item_id}
                    m={m}
                    forecast={f}
                    usageWindow={usageWindow}
                    onLeadTimeSaved={handleLeadTimeSaved}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* --- Charts tab --- */}
      {activeTab === 1 && (
        <ChartsTab rows={rows} usageWindow={usageWindow} leadDays={leadDays} />
      )}

      {/* --- Orders at risk tab --- */}
      {activeTab === 2 && (
        <OrdersTab orders={orderAnalysis} />
      )}
    </Container>
  );
};

export default StockProcurement;
